/* ==========================================================================
   FILE: modules/mind/components/MemoryList.jsx
   IAMONEAI - Memory List Component
   Displays and manages user memories with filtering and search
   ========================================================================== */

import React, { useState, useMemo, useCallback } from 'react';
import { Button, Input } from '../../../global/components';
import './MemoryList.css';

// ==========================================================================
// MEMORY TYPE CONFIGURATION
// ==========================================================================

const MEMORY_TYPES = {
  identity: { label: 'Identity', icon: '🪪', color: '#8B5CF6' },
  long_term: { label: 'Long Term', icon: '💾', color: '#3B82F6' },
  short_term: { label: 'Short Term', icon: '⚡', color: '#F59E0B' },
  episodic: { label: 'Episodic', icon: '📅', color: '#10B981' },
  preference: { label: 'Preference', icon: '❤️', color: '#EC4899' },
  skill: { label: 'Skill', icon: '🛠️', color: '#06B6D4' },
  belief: { label: 'Belief', icon: '💭', color: '#8B5CF6' },
  goal: { label: 'Goal', icon: '🎯', color: '#EF4444' },
  task: { label: 'Task', icon: '✅', color: '#84CC16' },
  emotion: { label: 'Emotion', icon: '😊', color: '#F97316' },
  system: { label: 'System', icon: '⚙️', color: '#6B7280' },
};

const EPISTEMIC_TYPES = {
  fact: { label: 'Fact', icon: '✓', color: '#10B981' },
  opinion: { label: 'Opinion', icon: '💬', color: '#3B82F6' },
  belief: { label: 'Belief', icon: '💭', color: '#8B5CF6' },
};

const CONTEXT_FILTERS = [
  { value: 'all', label: 'All Contexts' },
  { value: 'personal', label: '🏠 Personal' },
  { value: 'work', label: '💼 Work' },
  { value: 'family', label: '👨‍👩‍👧‍👦 Family' },
];

// ==========================================================================
// MEMORY LIST COMPONENT
// ==========================================================================

/**
 * Displays a filterable, searchable list of memories
 * 
 * @param {Object} props
 * @param {Array} memories - Array of memory objects
 * @param {Function} onMemoryClick - Called when a memory is clicked
 * @param {Function} onArchive - Called to archive a memory
 * @param {Function} onRefresh - Called to refresh memories
 * @param {boolean} loading - Loading state
 * @param {string} emptyMessage - Message when no memories
 */
function MemoryList({
  memories = [],
  onMemoryClick,
  onArchive,
  onRefresh,
  loading = false,
  emptyMessage = 'No memories yet. Start a conversation to build your memory!',
  className = '',
}) {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedContext, setSelectedContext] = useState('all');
  const [selectedEpistemic, setSelectedEpistemic] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // recent, importance, type

  // ==========================================================================
  // FILTERING & SORTING
  // ==========================================================================

  const filteredMemories = useMemo(() => {
    let filtered = [...memories];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.content?.toLowerCase().includes(query) ||
        m.summary?.toLowerCase().includes(query) ||
        m.context_tags?.some(t => t.toLowerCase().includes(query))
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(m => m.type === selectedType);
    }

    // Context filter
    if (selectedContext !== 'all') {
      filtered = filtered.filter(m => m.context_id === selectedContext);
    }

    // Epistemic filter
    if (selectedEpistemic !== 'all') {
      filtered = filtered.filter(m => m.epistemic_type === selectedEpistemic);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'importance':
          return (b.importance_score || 0) - (a.importance_score || 0);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        case 'recent':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

    return filtered;
  }, [memories, searchQuery, selectedType, selectedContext, selectedEpistemic, sortBy]);

  // Group by type for grouped view
  const groupedMemories = useMemo(() => {
    if (sortBy !== 'type') return null;
    
    const groups = {};
    filteredMemories.forEach(memory => {
      const type = memory.type || 'other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(memory);
    });
    return groups;
  }, [filteredMemories, sortBy]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedContext('all');
    setSelectedEpistemic('all');
  }, []);

  const hasActiveFilters = searchQuery || selectedType !== 'all' || 
    selectedContext !== 'all' || selectedEpistemic !== 'all';

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const listClasses = [
    'memory-list',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={listClasses}>
      {/* Header */}
      <div className="memory-list-header">
        <div className="memory-list-title-row">
          <h3 className="memory-list-title">
            <BrainIcon />
            Memories
            <span className="memory-list-count">{memories.length}</span>
          </h3>
          {onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              loading={loading}
              aria-label="Refresh memories"
            >
              <RefreshIcon />
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="memory-list-search">
          <Input
            type="search"
            placeholder="Search memories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<SearchIcon />}
          />
        </div>

        {/* Filters */}
        <div className="memory-list-filters">
          <select
            className="memory-filter-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            {Object.entries(MEMORY_TYPES).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>

          <select
            className="memory-filter-select"
            value={selectedContext}
            onChange={(e) => setSelectedContext(e.target.value)}
          >
            {CONTEXT_FILTERS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            className="memory-filter-select"
            value={selectedEpistemic}
            onChange={(e) => setSelectedEpistemic(e.target.value)}
          >
            <option value="all">All Knowledge</option>
            {Object.entries(EPISTEMIC_TYPES).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>

          <select
            className="memory-filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Most Recent</option>
            <option value="importance">Most Important</option>
            <option value="type">By Type</option>
          </select>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="memory-active-filters">
            <span className="memory-filter-count">
              {filteredMemories.length} of {memories.length} memories
            </span>
            <button
              className="memory-clear-filters"
              onClick={handleClearFilters}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Memory List */}
      <div className="memory-list-content">
        {loading ? (
          <div className="memory-list-loading">
            <LoadingSpinner />
            <span>Loading memories...</span>
          </div>
        ) : filteredMemories.length === 0 ? (
          <div className="memory-list-empty">
            <EmptyIcon />
            <p>{hasActiveFilters ? 'No memories match your filters' : emptyMessage}</p>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        ) : sortBy === 'type' && groupedMemories ? (
          // Grouped view
          Object.entries(groupedMemories).map(([type, typeMemories]) => (
            <div key={type} className="memory-group">
              <div className="memory-group-header">
                <span className="memory-group-icon">
                  {MEMORY_TYPES[type]?.icon || '📝'}
                </span>
                <span className="memory-group-label">
                  {MEMORY_TYPES[type]?.label || type}
                </span>
                <span className="memory-group-count">{typeMemories.length}</span>
              </div>
              <div className="memory-group-items">
                {typeMemories.map(memory => (
                  <MemoryItem
                    key={memory.id || memory.memoryId}
                    memory={memory}
                    onClick={() => onMemoryClick?.(memory)}
                    onArchive={() => onArchive?.(memory)}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // Flat list view
          <div className="memory-items">
            {filteredMemories.map(memory => (
              <MemoryItem
                key={memory.id || memory.memoryId}
                memory={memory}
                onClick={() => onMemoryClick?.(memory)}
                onArchive={() => onArchive?.(memory)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// MEMORY ITEM COMPONENT
// ==========================================================================

function MemoryItem({ memory, onClick, onArchive }) {
  const [showActions, setShowActions] = useState(false);

  const typeConfig = MEMORY_TYPES[memory.type] || MEMORY_TYPES.long_term;
  const epistemicConfig = EPISTEMIC_TYPES[memory.epistemic_type];
  
  const importance = memory.importance_score || 0;
  const createdAt = memory.created_at ? formatRelativeTime(memory.created_at) : null;

  return (
    <div 
      className="memory-item"
      onClick={onClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Type Badge */}
      <div 
        className="memory-item-type"
        style={{ backgroundColor: typeConfig.color }}
        title={typeConfig.label}
      >
        {typeConfig.icon}
      </div>

      {/* Content */}
      <div className="memory-item-content">
        <p className="memory-item-text">
          {memory.summary || memory.content}
        </p>
        
        <div className="memory-item-meta">
          {/* Context */}
          {memory.context_id && (
            <span className="memory-item-context">
              {memory.context_id === 'personal' && '🏠'}
              {memory.context_id === 'work' && '💼'}
              {memory.context_id === 'family' && '👨‍👩‍👧‍👦'}
              {memory.context_id}
            </span>
          )}

          {/* Epistemic Type */}
          {epistemicConfig && (
            <span 
              className="memory-item-epistemic"
              style={{ color: epistemicConfig.color }}
            >
              {epistemicConfig.icon} {epistemicConfig.label}
            </span>
          )}

          {/* Tags */}
          {memory.context_tags?.slice(0, 3).map(tag => (
            <span key={tag} className="memory-item-tag">#{tag}</span>
          ))}

          {/* Importance */}
          {importance > 0.7 && (
            <span className="memory-item-important" title="High importance">
              ⭐
            </span>
          )}

          {/* Time */}
          {createdAt && (
            <span className="memory-item-time">{createdAt}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && onArchive && (
        <div className="memory-item-actions">
          <button
            className="memory-item-action"
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            title="Archive memory"
          >
            <ArchiveIcon />
          </button>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

function formatRelativeTime(dateString) {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

// ==========================================================================
// ICONS
// ==========================================================================

function BrainIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" />
      <circle cx="12" cy="12" r="10" />
      <path d="M8 15h8" strokeLinecap="round" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div className="loading-spinner">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default MemoryList;
export { MemoryItem, MEMORY_TYPES, EPISTEMIC_TYPES };